import { useLayoutEffect } from "react";

/**
 * Ref-counted suppression registry. An inline execution surface (trade confirm
 * dialog, deposit/withdraw progress panel, money-movement tracking step,
 * operation detail dialog) acquires suppression for the operationId it is
 * showing; while held, the center toast for that operation keeps tracking but
 * stays hidden. On release, a still-active operation surfaces as a toast
 * (continuity after the modal closes); an operation that went terminal while
 * suppressed is never re-toasted — the inline surface already showed the
 * outcome.
 *
 * Module-level (not provider state) so surfaces can acquire before the
 * tracking record exists and independent of provider mount order.
 */
const suppressionCounts = new Map<string, number>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function acquireExecutionToastSuppression(
  operationId: string,
): () => void {
  suppressionCounts.set(
    operationId,
    (suppressionCounts.get(operationId) ?? 0) + 1,
  );
  notify();

  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;

    const count = suppressionCounts.get(operationId) ?? 0;
    if (count <= 1) {
      suppressionCounts.delete(operationId);
    } else {
      suppressionCounts.set(operationId, count - 1);
    }
    notify();
  };
}

export function isExecutionToastSuppressed(operationId: string): boolean {
  return (suppressionCounts.get(operationId) ?? 0) > 0;
}

export function subscribeExecutionToastSuppression(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Declarative suppression for inline execution surfaces. Pass the operationId
 * being rendered (or null/undefined while unknown) and whether the surface is
 * actually visible.
 */
export function useSuppressExecutionToast(
  operationId: string | null | undefined,
  active: boolean,
): void {
  // Layout effect: suppression must be acquired before paint so a toast
  // registered in the same commit as the inline surface never flashes.
  useLayoutEffect(() => {
    if (!operationId || !active) {
      return;
    }

    return acquireExecutionToastSuppression(operationId);
  }, [operationId, active]);
}
